// ============= Imports =============
import { createRelayHubLog } from '../shared/utils';
import type { RelayHubHandlers } from '../shared';
import { WorkspaceRelayHubEvents } from './events';
import type {
  WorkspaceRelayEventHandler,
  WorkspaceRelayHubOptions,
  WorkspaceReceiveMessageHandler,
  WorkspaceUploadEventPayload,
  WorkspaceUserPresenceHandler,
} from './types';
import {
  createWorkspaceEventHandler,
  getWorkspaceUploadEventPayload,
  normalizeWorkspaceUploadEvent,
  resolveToastNames,
  type WorkspaceEventHandlerContext,
} from './utils';

// ============= Handler Factory =============

/**
 * Creates handlers for all supported Workspace relay hub events and provides
 * subscribe/unsubscribe helpers. Uses shared logging and optional reusable
 * event handler for consistent behavior.
 */
export const createWorkspaceRelayHubHandlers = (
  handlers: RelayHubHandlers,
  options: WorkspaceRelayHubOptions = {},
) => {
  const {
    workspaceId,
    onDataRefresh,
    onMessageReceived,
    onEventReceived,
    toasts,
    enableToasts = true,
    enableLogging = true,
    getDisplayNameForUser,
    getWorkspaceNameById,
    currentUserId,
  } = options;

  const log = createRelayHubLog('WorkspaceRelayHub', enableLogging, '📁');

  const { showSuccess, showError, showInfo, showWarning } =
    enableToasts && toasts
      ? toasts
      : {
          showSuccess: () => {},
          showError: () => {},
          showInfo: () => {},
          showWarning: () => {},
        };

  const toastResolvers = { getDisplayNameForUser, getWorkspaceNameById };
  const ctx: WorkspaceEventHandlerContext = {
    workspaceId,
    log,
    onEventReceived,
    onMessageReceived,
    onDataRefresh,
    enableToasts,
    showSuccess,
    showError,
    showInfo,
    showWarning,
    getDisplayNameForUser,
    getWorkspaceNameById,
  };

  const getResolvedPresenceToastNames = (
    userId?: string,
    workspaceIdParam?: string,
  ) => {
    const { userName, workspaceName } = resolveToastNames(toastResolvers, {
      userId,
      workspaceId: workspaceIdParam,
    });

    const hasResolvedUserName =
      userId == null ||
      (getDisplayNameForUser != null && userName !== userId);
    const hasResolvedWorkspaceName =
      workspaceIdParam == null ||
      (getWorkspaceNameById != null && workspaceName !== workspaceIdParam);

    if (!hasResolvedUserName || !hasResolvedWorkspaceName) {
      return null;
    }

    return { userName, workspaceName };
  };

  // ----- File added (custom: 2-arg and 3-arg formats, fileName extraction) -----
  const handleFileAdded: WorkspaceRelayEventHandler = (
    param1,
    workspaceIdParam,
    userMessage,
  ) => {
    let actualWorkspaceId: string;
    let actualMessage: string | undefined;
    let actualUserMessage: WorkspaceUploadEventPayload | string | undefined;

    if (userMessage === undefined && typeof workspaceIdParam === 'string') {
      actualWorkspaceId =
        typeof param1 === 'string'
          ? param1
          : getWorkspaceUploadEventPayload(param1)?.workspaceId || 'unknown';
      actualMessage = workspaceIdParam;
      actualUserMessage = { message: workspaceIdParam };
    } else {
      actualWorkspaceId =
        workspaceIdParam ||
        (typeof param1 === 'string' ? param1 : 'unknown');
      actualUserMessage =
        typeof userMessage === 'string'
          ? userMessage
          : getWorkspaceUploadEventPayload(userMessage);
      const userMessagePayload = getWorkspaceUploadEventPayload(userMessage);
      actualMessage =
        typeof userMessage === 'string'
          ? userMessage
          : userMessagePayload?.message ||
            userMessagePayload?.fileName ||
            userMessagePayload?.name;
    }

    if (workspaceId && actualWorkspaceId !== workspaceId) return;

    let fileName = 'File';
    if (actualMessage) {
      const fileMatch = actualMessage.match(/File:\s*([^.]+\.\w+)/i);
      if (fileMatch) fileName = fileMatch[1];
    }
    if (actualUserMessage && typeof actualUserMessage !== 'string') {
      fileName =
        actualUserMessage.fileName ||
        actualUserMessage.name ||
        actualUserMessage.file?.name ||
        fileName;
    }

    const parsed = {
      workspaceId: actualWorkspaceId,
      param1,
      userMessage: actualUserMessage,
      message: actualMessage,
      fileName,
    };

    log(WorkspaceRelayHubEvents.DATAROOMFILE_ADDED_TO_WORKSPACE, {
      param1,
      workspaceIdParam,
      userMessage,
      actualWorkspaceId,
      actualMessage,
      fileName,
      workspaceIdFilter: workspaceId,
      willShowToast: workspaceId ? actualWorkspaceId === workspaceId : true,
    });

    if (onEventReceived) {
      onEventReceived(
        WorkspaceRelayHubEvents.DATAROOMFILE_ADDED_TO_WORKSPACE,
        {
          param1,
          workspaceId: actualWorkspaceId,
          userMessage: actualUserMessage,
          message: actualMessage,
        },
        parsed,
      );
    }

    if (enableToasts) {
      const toastMessage =
        actualMessage ||
        `${fileName} has been processed and added to the workspace`;
      showSuccess(toastMessage, 5000);
    }

    // Avoid double-refreshing: when a consumer provides onEventReceived, it can
    // perform a targeted refresh (e.g. update a single panel/list) instead of
    // forcing broader page-level reload behavior.
    if (!onEventReceived && onDataRefresh) void onDataRefresh();
  };

  // ----- Upload error handlers (support both 2-arg and 3-arg signatures) -----
  const handleFileUploadErrored: WorkspaceRelayEventHandler = (
    param1,
    workspaceIdParam,
    userMessage,
  ) => {
    const {
      actualWorkspaceId,
      actualMessage,
      actualUserMessage,
      fileName,
      errorText,
    } = normalizeWorkspaceUploadEvent(param1, workspaceIdParam, userMessage);

    if (workspaceId && actualWorkspaceId !== workspaceId) return;

    const parsed = {
      workspaceId: actualWorkspaceId,
      param1,
      userMessage: actualUserMessage,
      message: actualMessage,
      fileName,
      error: errorText,
    };

    log(WorkspaceRelayHubEvents.FILE_UPLOAD_ERRORED, {
      param1,
      workspaceIdParam,
      userMessage,
      actualWorkspaceId,
      actualMessage,
      fileName,
      errorText,
      workspaceIdFilter: workspaceId,
      willShowToast: workspaceId ? actualWorkspaceId === workspaceId : true,
    });

    if (onEventReceived) {
      onEventReceived(
        WorkspaceRelayHubEvents.FILE_UPLOAD_ERRORED,
        {
          param1,
          workspaceId: actualWorkspaceId,
          userMessage: actualUserMessage,
          message: actualMessage,
        },
        parsed,
      );
    }

    if (enableToasts) {
      const toastMessage = `${fileName} has failed due to potential error with the file, please check the file and try again`;
      showError(toastMessage, 7000);
    }

    if (onMessageReceived) {
      onMessageReceived({
        event: WorkspaceRelayHubEvents.FILE_UPLOAD_ERRORED,
        workspaceId: actualWorkspaceId,
        userMessage: actualUserMessage,
        parsed,
      });
    }
  };

  const handleUrlUploadErrored: WorkspaceRelayEventHandler = (
    param1,
    workspaceIdParam,
    userMessage,
  ) => {
    const {
      actualWorkspaceId,
      actualMessage,
      actualUserMessage,
      fileName,
      errorText,
    } = normalizeWorkspaceUploadEvent(param1, workspaceIdParam, userMessage);

    if (workspaceId && actualWorkspaceId !== workspaceId) return;

    const parsed = {
      workspaceId: actualWorkspaceId,
      param1,
      userMessage: actualUserMessage,
      message: actualMessage,
      error: errorText,
    };

    log(WorkspaceRelayHubEvents.URL_UPLOAD_ERRORED, {
      param1,
      workspaceIdParam,
      userMessage,
      actualWorkspaceId,
      actualMessage,
      errorText,
      workspaceIdFilter: workspaceId,
      willShowToast: workspaceId ? actualWorkspaceId === workspaceId : true,
    });

    if (onEventReceived) {
      onEventReceived(
        WorkspaceRelayHubEvents.URL_UPLOAD_ERRORED,
        {
          param1,
          workspaceId: actualWorkspaceId,
          userMessage: actualUserMessage,
          message: actualMessage,
        },
        parsed,
      );
    }

    if (enableToasts) {
      const toastMessage = `${fileName} has failed due to ${errorText || 'Unknown error'}`;
      showError(toastMessage, 7000);
    }

    if (onMessageReceived) {
      onMessageReceived({
        event: WorkspaceRelayHubEvents.URL_UPLOAD_ERRORED,
        workspaceId: actualWorkspaceId,
        userMessage: actualUserMessage,
        parsed,
      });
    }
  };

  const handleCentralUploadErrored: WorkspaceRelayEventHandler = (
    param1,
    workspaceIdParam,
    userMessage,
  ) => {
    const {
      actualWorkspaceId,
      actualMessage,
      actualUserMessage,
      errorText,
    } = normalizeWorkspaceUploadEvent(param1, workspaceIdParam, userMessage);

    if (workspaceId && actualWorkspaceId !== workspaceId) return;

    const parsed = {
      workspaceId: actualWorkspaceId,
      param1,
      userMessage: actualUserMessage,
      message: actualMessage,
      error: errorText,
    };

    log(WorkspaceRelayHubEvents.CENTRAL_UPLOAD_ERRORED, {
      param1,
      workspaceIdParam,
      userMessage,
      actualWorkspaceId,
      actualMessage,
      errorText,
      workspaceIdFilter: workspaceId,
      willShowToast: workspaceId ? actualWorkspaceId === workspaceId : true,
    });

    if (onEventReceived) {
      onEventReceived(
        WorkspaceRelayHubEvents.CENTRAL_UPLOAD_ERRORED,
        {
          param1,
          workspaceId: actualWorkspaceId,
          userMessage: actualUserMessage,
          message: actualMessage,
        },
        parsed,
      );
    }

    if (enableToasts) {
      const toastMessage = `Central upload failed: ${errorText}`;
      showError(toastMessage, 7000);
    }

    if (onMessageReceived) {
      onMessageReceived({
        event: WorkspaceRelayHubEvents.CENTRAL_UPLOAD_ERRORED,
        workspaceId: actualWorkspaceId,
        userMessage: actualUserMessage,
        parsed,
      });
    }
  };

  const createUploadRetryHandler = (
    event: WorkspaceRelayHubEvents.FILE_UPLOAD_RETRY | WorkspaceRelayHubEvents.URL_UPLOAD_RETRY,
  ) => {
    return (
      param1: unknown,
      workspaceIdParam?: unknown,
      userMessage?: unknown,
    ) => {
      const {
        actualWorkspaceId,
        actualMessage,
        actualUserMessage,
        fileName,
      } = normalizeWorkspaceUploadEvent(param1, workspaceIdParam, userMessage);

      if (workspaceId && actualWorkspaceId !== workspaceId) return;

      const parsed = {
        workspaceId: actualWorkspaceId,
        param1,
        userMessage: actualUserMessage,
        message: actualMessage,
        fileName,
      };

      log(event, {
        param1,
        workspaceIdParam,
        userMessage,
        actualWorkspaceId,
        actualMessage,
        fileName,
        workspaceIdFilter: workspaceId,
        willShowToast: workspaceId ? actualWorkspaceId === workspaceId : true,
      });

      if (onEventReceived) {
        onEventReceived(
          event,
          {
            param1,
            workspaceId: actualWorkspaceId,
            userMessage: actualUserMessage,
            message: actualMessage,
          },
          parsed,
        );
      }

      if (enableToasts) {
        showInfo(`${fileName} is taking longer to process`, 7000);
      }

      if (onMessageReceived) {
        onMessageReceived({
          event,
          workspaceId: actualWorkspaceId,
          userMessage: actualUserMessage,
          parsed,
        });
      }
    };
  };

  const handleFileUploadRetry = createUploadRetryHandler(
    WorkspaceRelayHubEvents.FILE_UPLOAD_RETRY,
  );

  const handleUrlUploadRetry = createUploadRetryHandler(
    WorkspaceRelayHubEvents.URL_UPLOAD_RETRY,
  );

  const handleDataRoomFileDeletedFromWorkspace = (
    param1: unknown,
    workspaceIdParam?: unknown,
    userMessage?: unknown,
  ) => {
    // Support both:
    //  - (workspaceId, messageOrPayload)
    //  - (fileIdOrPayload, workspaceId, userMessage)
    const getDeleteEventPayload = (
      payload: unknown,
    ): WorkspaceUploadEventPayload | undefined =>
      getWorkspaceUploadEventPayload(payload);

    const getDeleteEventMessage = (payload: unknown): string | undefined => {
      if (typeof payload === 'string') {
        return payload;
      }

      const eventPayload = getDeleteEventPayload(payload);
      return (
        eventPayload?.message || eventPayload?.fileName || eventPayload?.name
      );
    };

    let actualWorkspaceId: string;
    let actualUserMessage: WorkspaceUploadEventPayload | string | undefined;
    let actualMessage: string | undefined;

    if (userMessage === undefined) {
      actualWorkspaceId =
        typeof param1 === 'string'
          ? param1
          : getDeleteEventPayload(param1)?.workspaceId || 'unknown';
      actualUserMessage =
        typeof workspaceIdParam === 'string'
          ? workspaceIdParam
          : getDeleteEventPayload(workspaceIdParam);
      actualMessage = getDeleteEventMessage(workspaceIdParam);
    } else {
      actualWorkspaceId =
        typeof workspaceIdParam === 'string'
          ? workspaceIdParam
          : typeof param1 === 'string'
            ? param1
            : 'unknown';
      actualUserMessage =
        typeof userMessage === 'string'
          ? userMessage
          : getDeleteEventPayload(userMessage);
      actualMessage = getDeleteEventMessage(userMessage);
    }

    if (workspaceId && actualWorkspaceId !== workspaceId) return;

    const deleteEventPayload = getDeleteEventPayload(actualUserMessage);
    const fileId =
      deleteEventPayload?.fileId ||
      deleteEventPayload?.dataRoomFileId ||
      deleteEventPayload?.id ||
      (typeof param1 === 'string' && param1 !== actualWorkspaceId ? param1 : undefined) ||
      undefined;
    const fileName =
      deleteEventPayload?.fileName ||
      deleteEventPayload?.name ||
      deleteEventPayload?.file?.name ||
      undefined;

    const parsed = {
      workspaceId: actualWorkspaceId,
      param1,
      userMessage: actualUserMessage,
      message: actualMessage,
      fileId,
      fileName,
    };

    log(WorkspaceRelayHubEvents.DATAROOMFILE_DELETED_FROM_WORKSPACE, {
      param1,
      workspaceIdParam,
      userMessage,
      actualWorkspaceId,
      actualMessage,
      fileId,
      fileName,
      workspaceIdFilter: workspaceId,
      willShowToast: workspaceId ? actualWorkspaceId === workspaceId : true,
    });

    if (onEventReceived) {
      onEventReceived(
        WorkspaceRelayHubEvents.DATAROOMFILE_DELETED_FROM_WORKSPACE,
        {
          param1,
          workspaceId: actualWorkspaceId,
          userMessage: actualUserMessage,
          message: actualMessage,
        },
        parsed,
      );
    }

    if (enableToasts) {
      showInfo(actualMessage || `${fileName || 'File'} deleted from workspace`);
    }

    if (onDataRefresh) void onDataRefresh();

    if (onMessageReceived) {
      onMessageReceived({
        event: WorkspaceRelayHubEvents.DATAROOMFILE_DELETED_FROM_WORKSPACE,
        workspaceId: actualWorkspaceId,
        userMessage: actualUserMessage,
        parsed,
      });
    }
  };

  const handleDataRoomFileDeleteFromWorkspaceError =
    createWorkspaceEventHandler(ctx, {
      event: WorkspaceRelayHubEvents.DATAROOMFILE_DELETE_FROM_WORKSPACE_ERROR,
      getParsed: (param1, workspaceIdParam, userMessage) => ({
        workspaceId: workspaceIdParam || param1 || 'unknown',
        param1,
        userMessage,
        error: getWorkspaceUploadEventPayload(userMessage)?.error || 'Unknown error',
      }),
      toastType: 'error',
      getToastMessage: (p) =>
        `File delete failed: ${(p.error as string) || 'Unknown error'}`,
      includeOnMessageReceived: true,
    });

  const handleWorkflowAddedToWorkspace = createWorkspaceEventHandler(ctx, {
    event: WorkspaceRelayHubEvents.WORKFLOW_ADDED_TO_WORKSPACE,
    getParsed: (param1, workspaceIdParam, userMessage) => ({
      workspaceId: workspaceIdParam || param1 || 'unknown',
      param1,
      userMessage,
    }),
    toastType: 'success',
    getToastMessage: () => 'Workflow added to workspace',
    includeDataRefresh: true,
  });

  const handleWorkflowDeletedFromWorkspace = createWorkspaceEventHandler(ctx, {
    event: WorkspaceRelayHubEvents.WORKFLOW_DELETED_FROM_WORKSPACE,
    getParsed: (param1, workspaceIdParam, userMessage) => ({
      workspaceId: workspaceIdParam || param1 || 'unknown',
      param1,
      userMessage,
    }),
    toastType: 'info',
    getToastMessage: () => 'Workflow deleted from workspace',
    includeDataRefresh: true,
  });

  const handleAskDeletedFromWorkspace = createWorkspaceEventHandler(ctx, {
    event: WorkspaceRelayHubEvents.ASK_SESSION_DELETED_FROM_WORKSPACE,
    getParsed: (param1, workspaceIdParam, userMessage) => ({
      workspaceId: workspaceIdParam || param1 || 'unknown',
      param1,
      userMessage,
    }),
    toastType: 'success',
    getToastMessage: (p) =>
      `Ask session [${(p.param1 as { askTitle?: string })?.askTitle ?? 'Unknown'}] deleted from workspace`,
    includeDataRefresh: true,
  });

  const handleAskAddedToWorkspace = createWorkspaceEventHandler(ctx, {
    event: WorkspaceRelayHubEvents.ASK_SESSION_ADDED_TO_WORKSPACE,
    getParsed: (param1, workspaceIdParam, userMessage) => ({
      workspaceId: workspaceIdParam || param1 || 'unknown',
      param1,
      userMessage,
    }),
    toastType: 'success',
    getToastMessage: () => 'Ask session added to the workspace',
    includeDataRefresh: true,
  });

  const handleAskUpdatedInWorkspace = createWorkspaceEventHandler(ctx, {
    event: WorkspaceRelayHubEvents.ASK_SESSION_UPDATED_IN_WORKSPACE,
    getParsed: (param1, workspaceIdParam, userMessage) => ({
      workspaceId: workspaceIdParam || param1 || 'unknown',
      param1,
      userMessage,
    }),
    toastType: 'info',
    getToastMessage: () => 'Ask session updated in workspace',
    includeDataRefresh: true,
  });

  const handleCollectionAddedToWorkspace = createWorkspaceEventHandler(ctx, {
    event: WorkspaceRelayHubEvents.COLLECTION_ADDED_TO_WORKSPACE,
    getParsed: (param1, workspaceIdParam, userMessage) => ({
      workspaceId: workspaceIdParam || param1 || 'unknown',
      param1,
      userMessage,
    }),
    toastType: 'success',
    getToastMessage: () => 'Collection added to workspace',
    includeDataRefresh: true,
  });

  const handleCollectionDeletedFromWorkspace = createWorkspaceEventHandler(
    ctx,
    {
      event: WorkspaceRelayHubEvents.COLLECTION_DELETED_FROM_WORKSPACE,
      getParsed: (param1, workspaceIdParam, userMessage) => ({
        workspaceId: workspaceIdParam || param1 || 'unknown',
        param1,
        userMessage,
      }),
      toastType: 'info',
      getToastMessage: () => 'Collection deleted from workspace',
      includeDataRefresh: true,
    },
  );

  const handleCollectionUpdatedInWorkspace = createWorkspaceEventHandler(ctx, {
    event: WorkspaceRelayHubEvents.COLLECTION_UPDATED,
    getParsed: (param1, workspaceIdParam, userMessage) => ({
      workspaceId: workspaceIdParam || param1 || 'unknown',
      param1,
      userMessage,
    }),
    toastType: 'info',
    getToastMessage: () => 'Collection updated in workspace',
    includeDataRefresh: true,
  });

  // ----- Message and presence (custom signatures) -----
  const handleReceiveMessage: WorkspaceReceiveMessageHandler = (
    workspaceIdParam,
    senderId,
    message,
  ) => {
    const workspaceIdStr =
      typeof workspaceIdParam === 'string'
        ? workspaceIdParam
        : String(workspaceIdParam ?? '');

    if (workspaceId && workspaceIdStr !== workspaceId) return;

    const parsed = {
      workspaceId: workspaceIdParam || 'unknown',
      senderId: senderId || 'unknown',
      message: message || 'unknown',
    };

    log(WorkspaceRelayHubEvents.RECEIVE_MESSAGE, {
      workspaceId: workspaceIdParam,
      senderId,
      message,
    });

    if (onEventReceived) {
      onEventReceived(
        WorkspaceRelayHubEvents.RECEIVE_MESSAGE,
        { workspaceId: workspaceIdParam, senderId, message },
        parsed,
      );
    }
    if (onMessageReceived) {
      onMessageReceived({ workspaceId: workspaceIdParam, senderId, message });
    }
    if (enableToasts) {
      const { workspaceName } = resolveToastNames(toastResolvers, {
        workspaceId: workspaceIdStr || undefined,
      });
      showInfo(`Message in workspace ${workspaceName}: ${String(message ?? '')}`);
    }
  };

  // When USER_ONLINE fires, onEventReceived is called → WorkspaceContext handleWorkspacePresenceEvent
  // → updateUserOnlineStatus(workspaceId, userId, true) → setTransformedWorkspaces. That triggers
  // workspaceItemsWithPresence and getTransformedWorkspaceById to update, so WorkspaceHeaderLayout
  // and LandingPage re-render with new isOnline and the avatar/UI updates visually.
  const handleUserOnline: WorkspaceUserPresenceHandler = (
    workspaceIdParam,
    userId,
    userData,
  ) => {
    if (workspaceId && workspaceIdParam !== workspaceId) return;

    const parsed = {
      workspaceId: workspaceIdParam || 'unknown',
      userId: userId || 'unknown',
      userData: userData || null,
    };

    log(WorkspaceRelayHubEvents.USER_ONLINE, {
      workspaceId: workspaceIdParam,
      userId,
      userData,
    });

    if (onEventReceived) {
      onEventReceived(
        WorkspaceRelayHubEvents.USER_ONLINE,
        { workspaceId: workspaceIdParam, userId, userData },
        parsed,
      );
    }
    // Don't toast when the user coming online is the current user (self)
    if (enableToasts && (!currentUserId || userId !== currentUserId)) {
      const names = getResolvedPresenceToastNames(userId, workspaceIdParam);
      if (names) {
        showInfo(
          `User ${names.userName} is now online in workspace ${names.workspaceName}`,
        );
      }
    }
  };

  // Same chain as USER_ONLINE: onEventReceived → updateUserOnlineStatus(..., false) → UI re-renders with user offline.
  const handleUserOffline = (workspaceIdParam: string, userId: string) => {
    if (workspaceId && workspaceIdParam !== workspaceId) return;

    const parsed = {
      workspaceId: workspaceIdParam || 'unknown',
      userId: userId || 'unknown',
    };

    log(WorkspaceRelayHubEvents.USER_OFFLINE, {
      workspaceId: workspaceIdParam,
      userId,
    });

    if (onEventReceived) {
      onEventReceived(
        WorkspaceRelayHubEvents.USER_OFFLINE,
        { workspaceId: workspaceIdParam, userId },
        parsed,
      );
    }
    // Don't toast when the user going offline is the current user (self)
    if (enableToasts && (!currentUserId || userId !== currentUserId)) {
      const names = getResolvedPresenceToastNames(userId, workspaceIdParam);
      if (names) {
        showInfo(
          `User ${names.userName} is now offline in workspace ${names.workspaceName}`,
        );
      }
    }
  };

  const handleUserConnectedToWorkspace = (
    workspaceIdParam: string,
    userId: string,
  ) => {
    if (workspaceId && workspaceIdParam !== workspaceId) return;

    const parsed = {
      workspaceId: workspaceIdParam || 'unknown',
      userId: userId || 'unknown',
    };

    log(WorkspaceRelayHubEvents.USER_CONNECTED_TO_WORKSPACE, {
      workspaceId: workspaceIdParam,
      userId,
    });

    if (onEventReceived) {
      onEventReceived(
        WorkspaceRelayHubEvents.USER_CONNECTED_TO_WORKSPACE,
        { workspaceId: workspaceIdParam, userId },
        parsed,
      );
    }

    // Don't toast when the user connecting is the current user (self)
    if (enableToasts && (!currentUserId || userId !== currentUserId)) {
      const names = getResolvedPresenceToastNames(userId, workspaceIdParam);
      if (names) {
        showInfo(
          `User ${names.userName} is now connected to workspace ${names.workspaceName}`,
        );
      }
    }
    if (onDataRefresh) void onDataRefresh();
  };

  const handleUserDisconnectedFromWorkspace = (
    workspaceIdParam: string,
    userId: string,
  ) => {
    if (workspaceId && workspaceIdParam !== workspaceId) return;

    const parsed = {
      workspaceId: workspaceIdParam || 'unknown',
      userId: userId || 'unknown',
    };

    log(WorkspaceRelayHubEvents.USER_DISCONNECTED_FROM_WORKSPACE, {
      workspaceId: workspaceIdParam,
      userId,
    });

    if (onEventReceived) {
      onEventReceived(
        WorkspaceRelayHubEvents.USER_DISCONNECTED_FROM_WORKSPACE,
        { workspaceId: workspaceIdParam, userId },
        parsed,
      );
    }
    // Don't toast when the user disconnecting is the current user (self)
    if (enableToasts && (!currentUserId || userId !== currentUserId)) {
      const names = getResolvedPresenceToastNames(userId, workspaceIdParam);
      if (names) {
        showInfo(
          `User ${names.userName} is now disconnected from workspace ${names.workspaceName}`,
        );
      }
    }
    if (onDataRefresh) void onDataRefresh();
  };

  // ----- Subscribe / Unsubscribe -----
  type WorkspaceRegisteredHandler =
    | WorkspaceRelayEventHandler
    | WorkspaceReceiveMessageHandler
    | WorkspaceUserPresenceHandler
    | ((workspaceIdParam: string, userId: string) => void);

  type SignalRHandlerRegistration = Parameters<RelayHubHandlers['on']>[1];

  const eventHandlers: Array<[string, WorkspaceRegisteredHandler]> = [
    [WorkspaceRelayHubEvents.DATAROOMFILE_ADDED_TO_WORKSPACE, handleFileAdded],
    [WorkspaceRelayHubEvents.FILE_UPLOAD_ERRORED, handleFileUploadErrored],
    [WorkspaceRelayHubEvents.FILE_UPLOAD_RETRY, handleFileUploadRetry],
    [WorkspaceRelayHubEvents.URL_UPLOAD_ERRORED, handleUrlUploadErrored],
    [WorkspaceRelayHubEvents.URL_UPLOAD_RETRY, handleUrlUploadRetry],
    [
      WorkspaceRelayHubEvents.CENTRAL_UPLOAD_ERRORED,
      handleCentralUploadErrored,
    ],
    [
      WorkspaceRelayHubEvents.DATAROOMFILE_DELETED_FROM_WORKSPACE,
      handleDataRoomFileDeletedFromWorkspace,
    ],
    [
      WorkspaceRelayHubEvents.DATAROOMFILE_DELETE_FROM_WORKSPACE_ERROR,
      handleDataRoomFileDeleteFromWorkspaceError,
    ],
    [
      WorkspaceRelayHubEvents.WORKFLOW_ADDED_TO_WORKSPACE,
      handleWorkflowAddedToWorkspace,
    ],
    [
      WorkspaceRelayHubEvents.WORKFLOW_DELETED_FROM_WORKSPACE,
      handleWorkflowDeletedFromWorkspace,
    ],
    [
      WorkspaceRelayHubEvents.ASK_SESSION_DELETED_FROM_WORKSPACE,
      handleAskDeletedFromWorkspace,
    ],
    [
      WorkspaceRelayHubEvents.ASK_SESSION_ADDED_TO_WORKSPACE,
      handleAskAddedToWorkspace,
    ],
    [
      WorkspaceRelayHubEvents.ASK_SESSION_UPDATED_IN_WORKSPACE,
      handleAskUpdatedInWorkspace,
    ],
    [
      WorkspaceRelayHubEvents.COLLECTION_ADDED_TO_WORKSPACE,
      handleCollectionAddedToWorkspace,
    ],
    [
      WorkspaceRelayHubEvents.COLLECTION_DELETED_FROM_WORKSPACE,
      handleCollectionDeletedFromWorkspace,
    ],
    [
      WorkspaceRelayHubEvents.COLLECTION_UPDATED,
      handleCollectionUpdatedInWorkspace,
    ],
    [WorkspaceRelayHubEvents.RECEIVE_MESSAGE, handleReceiveMessage],
    [WorkspaceRelayHubEvents.USER_ONLINE, handleUserOnline],
    [WorkspaceRelayHubEvents.USER_OFFLINE, handleUserOffline],
    [
      WorkspaceRelayHubEvents.USER_CONNECTED_TO_WORKSPACE,
      handleUserConnectedToWorkspace,
    ],
    [
      WorkspaceRelayHubEvents.USER_DISCONNECTED_FROM_WORKSPACE,
      handleUserDisconnectedFromWorkspace,
    ],
  ];

  const subscribe = () => {
    eventHandlers.forEach(([event, handler]) =>
      handlers.on(event, handler as SignalRHandlerRegistration),
    );
  };

  const unsubscribe = () => {
    eventHandlers.forEach(([event, handler]) =>
      handlers.off(event, handler as SignalRHandlerRegistration),
    );
  };

  return {
    subscribe,
    unsubscribe,
    handlers: {
      handleFileAdded,
      handleFileUploadErrored,
      handleFileUploadRetry,
      handleUrlUploadErrored,
      handleUrlUploadRetry,
      handleCentralUploadErrored,
      handleDataRoomFileDeletedFromWorkspace,
      handleDataRoomFileDeleteFromWorkspaceError,
      handleWorkflowAddedToWorkspace,
      handleWorkflowDeletedFromWorkspace,
      handleAskDeletedFromWorkspace,
      handleAskAddedToWorkspace,
      handleAskUpdatedInWorkspace,
      handleCollectionAddedToWorkspace,
      handleCollectionDeletedFromWorkspace,
      handleCollectionUpdatedInWorkspace,
      handleReceiveMessage,
      handleUserOnline,
      handleUserOffline,
      handleUserConnectedToWorkspace,
      handleUserDisconnectedFromWorkspace,
    },
  };
};
